<template>
  <section class="sale-summary-tab">
    <div class="sale-summary-tab__grid">
      <div class="sale-summary-tab__main">
        <SaleSummaryExpensesProgressCard
          :budget-amount="budgetAmount"
          :spent-amount="spentAmount"
          :spent-paid-amount="spentPaidAmount"
          :available-amount="availableAmount"
          :available-real-amount="availableRealAmount"
          :progress-percent="progressPercent"
          :progress-percent-raw="progressPercentRaw"
          :paid-progress-percent="paidProgressPercent"
          :paid-progress-percent-raw="paidProgressPercentRaw"
          :expense-status="expenseStatus"
        />

        <SaleSummaryIncomeProgressCard
          :revenue-target-amount="revenueTargetAmount"
          :revenue-collected-amount="revenueCollectedAmount"
          :revenue-pending-amount="revenuePendingAmount"
          :revenue-progress-percent="revenueProgressPercent"
          :revenue-progress-percent-raw="revenueProgressPercentRaw"
          :income-status="incomeStatus"
        />

        <RecentActivityCard
          :entity-id="saleId"
          entity-type="sale"
          @view-history="$emit('view-history')"
        />
      </div>

      <aside class="sale-summary-tab__side">
        <SaleSummaryBusinessHealthCard
          :ring-percent="ringPercent"
          :margin-percent="marginPercent"
          :ring-main-amount="ringMainAmount"
          :ring-total-amount="ringTotalAmount"
          :health-status="healthStatus"
          :health-summary-text="healthSummaryText"
          :ring-color="ringColor"
        />

        <PaymentFlowCard
          :sale-id="saleId"
          :is-multi-customer-sale="isMultiCustomerSale"
        />

        <article class="sale-summary-card sale-summary-card--operational">
          <header class="sale-summary-card__section-title">
            <h5>{{ operationalText.title }}</h5>
          </header>

          <div class="sale-summary-operational">
            <div class="sale-summary-operational__item">
              <span>{{ operationalText.service }}</span>
              <strong>{{ operationalService }}</strong>
            </div>

            <div class="sale-summary-operational__item">
              <span>{{ operationalText.manager }}</span>
              <div v-if="hasOperationalManager" class="sale-summary-operational__manager-row">
                <i class="sale-summary-operational__avatar" aria-hidden="true">{{ operationalManagerInitials }}</i>
                <strong>{{ operationalManager }}</strong>
              </div>
              <button
                v-else
                type="button"
                class="sale-summary-operational__assign-btn"
                @click="openResponsibleEditor"
              >
                {{ operationalText.assign }}
              </button>
            </div>

            <div class="sale-summary-operational__item">
              <span>{{ operationalText.keyDates }}</span>
              <div class="sale-summary-operational__dates">
                <p>
                  <small>{{ operationalText.saleDate }}</small>
                  <strong>{{ operationalStartDate }}</strong>
                </p>
                <p>
                  <small>{{ operationalText.deliveryDate }}</small>
                  <strong>{{ operationalDeliveryDate }}</strong>
                </p>
              </div>
            </div>
          </div>
        </article>
      </aside>
    </div>
  </section>
</template>

<script>
import { formatCurrency } from '@/utils/numberFormat';
import { formatDateShort } from '@/utils/datePresentation';
import { extractPayload } from '@/utils/dataNormalization';
import salesService from '@/services/sales';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import { SALES_TEXT } from '@/texts/catalog/sales';
import SaleSummaryExpensesProgressCard from '@/components/sales/shared/summary/SaleSummaryExpensesProgressCard.vue';
import SaleSummaryIncomeProgressCard from '@/components/sales/shared/summary/SaleSummaryIncomeProgressCard.vue';
import SaleSummaryBusinessHealthCard from '@/components/sales/shared/summary/SaleSummaryBusinessHealthCard.vue';
import RecentActivityCard from '@/components/common/RecentActivityCard.vue';
import PaymentFlowCard from '@/components/common/PaymentFlowCard.vue';

const SALES_SUMMARY_TEXT = SALES_TEXT.shared.summary;
const OPERATIONAL_TEXT = SALES_SUMMARY_TEXT.tab.operational;

export default {
  name: 'SaleSharedSummaryTab',
  components: {
    SaleSummaryExpensesProgressCard,
    SaleSummaryIncomeProgressCard,
    SaleSummaryBusinessHealthCard,
    RecentActivityCard,
    PaymentFlowCard,
  },
  props: {
    sale: {
      type: Object,
      default: null,
    },
  },
  emits: ['register-plan-payment', 'new-expense', 'new-fee', 'view-history', 'edit-section'],
  data() {
    return {
      operationalText: OPERATIONAL_TEXT,
      progress: {
        expense: {
          budget_total: 0,
          spent_total: 0,
          available_total: 0,
          percent: 0,
          percent_raw: 0,
          status: {
            label: OPERATIONAL_TEXT.defaults.healthyStatus,
            tone: 'success',
          },
        },
        income: {
          target_total: 0,
          collected_total: 0,
          pending_total: 0,
          percent: 0,
          percent_raw: 0,
          status: {
            label: OPERATIONAL_TEXT.defaults.criticalStatus,
            tone: 'danger',
          },
        },
        health: {
          margin_percent: 0,
          utility_total: 0,
          income_total: 0,
          spent_total: 0,
          ring_percent: 0,
          ring_color: '#22c55e',
          ring_main_label: '0.0%',
          ring_total_label: '',
          summary_text: '',
          status: {
            label: OPERATIONAL_TEXT.defaults.unhealthyStatus,
            tone: 'danger',
          },
        },
      },
    };
  },
  watch: {
    saleId: {
      immediate: true,
      handler() {
        this.loadProgress();
      },
    },
    isMultiCustomerSale() {
      this.loadProgress();
    },
  },
  computed: {
    saleId() {
      return Number(this.sale?.id || 0);
    },
    isMultiCustomerSale() {
      const countCandidates = [
        this.sale?.customers_count,
        this.sale?.companies_count,
        this.sale?.clients_count,
      ];

      const hasManyCustomers = countCandidates.some((candidate) => Number(candidate || 0) > 1);
      const hasCustomersArray = Array.isArray(this.sale?.customers) && this.sale.customers.length > 1;
      const multiFlag = Number(this.sale?.is_multi_customer || this.sale?.multi_customer || 0) === 1;
      const typeLabel = String(this.sale?.sale_type || this.sale?.type || this.sale?.sale_mode || '').trim().toLowerCase();

      return hasManyCustomers || hasCustomersArray || multiFlag || typeLabel.includes('multi');
    },
    budgetAmount() {
      return Number(this.progress?.expense?.budget_total || 0) || 0;
    },
    spentAmount() {
      return Number(this.progress?.expense?.spent_total || 0) || 0;
    },
    spentPaidAmount() {
      return Number(this.progress?.expense?.spent_paid_total || 0) || 0;
    },
    availableAmount() {
      return Number(this.progress?.expense?.available_total || 0) || 0;
    },
    availableRealAmount() {
      return Number(this.progress?.expense?.available_real_total || 0) || 0;
    },
    progressPercent() {
      return Number(this.progress?.expense?.percent || 0) || 0;
    },
    progressPercentRaw() {
      return Number(this.progress?.expense?.percent_raw || this.progressPercent || 0) || 0;
    },
    paidProgressPercent() {
      return Number(this.progress?.expense?.paid_percent || 0) || 0;
    },
    paidProgressPercentRaw() {
      return Number(this.progress?.expense?.paid_percent_raw || this.paidProgressPercent || 0) || 0;
    },
    expenseStatus() {
      const label = String(this.progress?.expense?.status?.label || '').trim() || this.operationalText.defaults.healthyStatus;
      const tone = String(this.progress?.expense?.status?.tone || '').trim().toLowerCase();
      return {
        label,
        className: this.toToneClass(tone),
      };
    },
    revenueTargetAmount() {
      return Number(this.progress?.income?.target_total || 0) || 0;
    },
    revenueCollectedAmount() {
      return Number(this.progress?.income?.collected_total || 0) || 0;
    },
    revenuePendingAmount() {
      return Number(this.progress?.income?.pending_total || 0) || 0;
    },
    revenueProgressPercent() {
      return Number(this.progress?.income?.percent || 0) || 0;
    },
    revenueProgressPercentRaw() {
      return Number(this.progress?.income?.percent_raw || this.revenueProgressPercent || 0) || 0;
    },
    revenueProgressLabel() {
      return `${this.revenueProgressPercentRaw.toFixed(1)}%`;
    },
    incomeStatus() {
      const label = String(this.progress?.income?.status?.label || '').trim() || this.operationalText.defaults.criticalStatus;
      const tone = String(this.progress?.income?.status?.tone || '').trim().toLowerCase();
      return {
        label,
        className: this.toToneClass(tone),
      };
    },
    ringPercent() {
      return Number(this.progress?.health?.ring_percent || 0) || 0;
    },
    marginPercent() {
      return Number(this.progress?.health?.margin_percent || 0) || 0;
    },
    ringColor() {
      return String(this.progress?.health?.ring_color || '').trim() || '#22c55e';
    },
    ringMainAmount() {
      return String(this.progress?.health?.ring_main_label || '').trim() || `${this.ringPercent}%`;
    },
    ringTotalAmount() {
      return String(this.progress?.health?.ring_total_label || '').trim();
    },
    healthStatus() {
      const label = String(this.progress?.health?.status?.label || '').trim() || this.operationalText.defaults.unhealthyStatus;
      const tone = String(this.progress?.health?.status?.tone || '').trim().toLowerCase();

      return {
        label,
        className: this.toToneClass(tone),
      };
    },
    healthSummaryText() {
      return String(this.progress?.health?.summary_text || '').trim()
        || SALES_SUMMARY_TEXT.businessHealthCard.defaults.summary;
    },
    operationalService() {
      return String(this.sale?.product_name || this.sale?.service_type_name || '').trim()
        || this.operationalText.defaults.service;
    },
    operationalManager() {
      return String(this.sale?.manager_name || this.sale?.owner_name || '').trim();
    },
    hasOperationalManager() {
      return this.operationalManager !== '';
    },
    operationalManagerInitials() {
      const fullName = this.operationalManager;
      if (fullName === '') {
        return this.operationalText.defaults.managerInitials;
      }

      const parts = fullName
        .split(/\s+/)
        .map((part) => String(part || '').trim())
        .filter((part) => part !== '');

      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }

      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    },
    operationalStartDate() {
      const value = formatDateShort(this.sale?.sale_date || this.sale?.created_at);
      return String(value || '').trim() || this.operationalText.defaults.saleDate;
    },
    operationalDeliveryDate() {
      const value = formatDateShort(this.sale?.delivery_date);
      return String(value || '').trim() || this.operationalText.defaults.deliveryDate;
    },
  },
  methods: {
    async loadProgress() {
      const saleId = Number(this.saleId || 0);
      if (saleId <= 0) {
        this.resetProgress();
        return;
      }

      try {
        const response = this.isMultiCustomerSale
          ? await salesMultiCustomerService.progress(saleId)
          : await salesService.progress(saleId);

        const payload = extractPayload(response);
        this.progress = {
          expense: {
            budget_total: Number(payload?.expense?.budget_total || 0) || 0,
            spent_total: Number(payload?.expense?.spent_total || 0) || 0,
            spent_paid_total: Number(payload?.expense?.spent_paid_total || 0) || 0,
            available_total: Number(payload?.expense?.available_total || 0) || 0,
            available_real_total: Number(payload?.expense?.available_real_total || 0) || 0,
            percent: Number(payload?.expense?.percent || 0) || 0,
            percent_raw: Number(payload?.expense?.percent_raw || payload?.expense?.percent || 0) || 0,
            paid_percent: Number(payload?.expense?.paid_percent || 0) || 0,
            paid_percent_raw: Number(payload?.expense?.paid_percent_raw || payload?.expense?.paid_percent || 0) || 0,
            status: {
              label: String(payload?.expense?.status?.label || '').trim() || this.operationalText.defaults.healthyStatus,
              tone: String(payload?.expense?.status?.tone || '').trim().toLowerCase() || 'success',
            },
          },
          income: {
            target_total: Number(payload?.income?.target_total || 0) || 0,
            collected_total: Number(payload?.income?.collected_total || 0) || 0,
            pending_total: Number(payload?.income?.pending_total || 0) || 0,
            percent: Number(payload?.income?.percent || 0) || 0,
            percent_raw: Number(payload?.income?.percent_raw || payload?.income?.percent || 0) || 0,
            status: {
              label: String(payload?.income?.status?.label || '').trim() || this.operationalText.defaults.criticalStatus,
              tone: String(payload?.income?.status?.tone || '').trim().toLowerCase() || 'danger',
            },
          },
          health: {
            margin_percent: Number(payload?.health?.margin_percent || 0) || 0,
            utility_total: Number(payload?.health?.utility_total || 0) || 0,
            income_total: Number(payload?.health?.income_total || 0) || 0,
            spent_total: Number(payload?.health?.spent_total || 0) || 0,
            ring_percent: Number(payload?.health?.ring_percent || 0) || 0,
            ring_color: String(payload?.health?.ring_color || '').trim() || '#22c55e',
            ring_main_label: String(payload?.health?.ring_main_label || '').trim() || '0.0%',
            ring_total_label: String(payload?.health?.ring_total_label || '').trim(),
            summary_text: String(payload?.health?.summary_text || '').trim(),
            status: {
              label: String(payload?.health?.status?.label || '').trim() || this.operationalText.defaults.unhealthyStatus,
              tone: String(payload?.health?.status?.tone || '').trim().toLowerCase() || 'danger',
            },
          },
        };
      } catch (error) {
        this.resetProgress();
      }
    },
    resetProgress() {
      this.progress = {
        expense: {
          budget_total: 0,
          spent_total: 0,
          spent_paid_total: 0,
          available_total: 0,
          available_real_total: 0,
          percent: 0,
          percent_raw: 0,
          paid_percent: 0,
          paid_percent_raw: 0,
          status: {
            label: this.operationalText.defaults.healthyStatus,
            tone: 'success',
          },
        },
        income: {
          target_total: 0,
          collected_total: 0,
          pending_total: 0,
          percent: 0,
          percent_raw: 0,
          status: {
            label: this.operationalText.defaults.criticalStatus,
            tone: 'danger',
          },
        },
        health: {
          margin_percent: 0,
          utility_total: 0,
          income_total: 0,
          spent_total: 0,
          ring_percent: 0,
          ring_color: '#22c55e',
          ring_main_label: '0.0%',
          ring_total_label: '',
          summary_text: '',
          status: {
            label: this.operationalText.defaults.unhealthyStatus,
            tone: 'danger',
          },
        },
      };
    },
    toToneClass(tone) {
      if (tone === 'success') {
        return 'is-success';
      }

      if (tone === 'warning') {
        return 'is-warning';
      }

      return 'is-danger';
    },
    openResponsibleEditor() {
      this.$emit('edit-section', 'configuration');
    },
  },
};
</script>
